import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    Inject, OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FeedbackAthleteAnswerProvider } from '../../providers/feedback-athlete-answer-provider';
import { SimpleMatDataSource } from '../../../common/providers/simple-mat-data-source';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import * as athleteAnswersComponents from '../../athlete-answers/athlete-answers';
import BaseAthleteAnswer from '../../athlete-answers/base-athlete-answer';
import { ArrayUtils } from "../../../common/providers/array-utils";
import { FeedbackRights, mapFeedbackItemRightsToRowRights } from '../../providers/feedback-rights';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { CurrentUser } from '../../../common/providers/current-user';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { map, Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { importToArray, UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AppDatePipe } from "../../../common/pipes/app-date";
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { User } from "../../../common/models/user";
import { ApiBaseService } from "../../../common/providers/api-base-service";
import { OrganizationRole } from "../../../common/models/organization-role";
import { MatDivider } from "@angular/material/divider";
import { MatCardContent } from "@angular/material/card";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { AthleteSideMenuComponent } from "../../../athletes/components/side-menu/side-menu";
import { KeyValuePipe, NgForOf, NgIf } from "@angular/common";

const ATHLETE_ANSWER_COMPONENTS = importToArray(athleteAnswersComponents);
const ATHLETE_ANSWER_COMPONENTS_MAP = ATHLETE_ANSWER_COMPONENTS.reduce<any>((result:any, value:any, index, array) => {
    result[value.type] = value;
    return result;
}, {});

@Component({
  selector: 'app-feedback-show-container',
  templateUrl: './feedback-show-container.component.html',
  styleUrls: ['./feedback-show-container.scss'],
  standalone: true,
  imports: [
    MatDivider,
    MatCardContent,
    ModuleContentComponent,
    Error404Component,
    Error401Component,
    SideMenuComponent,
    HeaderComponent,
    PageContentComponent,
    AthleteSideMenuComponent,
    NgIf,
    TranslateModule,
    KeyValuePipe,
    AppDatePipe,
    NgForOf
  ],
  providers: [AppDatePipe]
})
export class FeedbackShowContainerComponent implements OnInit, AfterViewInit, OnDestroy {
    public pageTitle = 'Tests';
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public array = Array;

    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public columns: string[] = ['name', 'createDate', 'smiley'];

    public organization: Organization = {} as Organization;
    public feedbackAthleteAnswer: any;
    public athleteUser: User = {} as User;
    public rights: FeedbackRights = new FeedbackRights(this.utils);

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;

    @ViewChildren('athleteAnswerRef', { read: ViewContainerRef })
    public athleteAnswerRefs: QueryList<ViewContainerRef> = new QueryList<ViewContainerRef>();

    public answers :any;

    private factoryResolver;

    private componentRef : any;
    private subscriptions: Subscription[] = [];

    constructor(
    @Inject(ComponentFactoryResolver) factoryResolver: ComponentFactoryResolver,
        private feedbackAthleteAnswerProvider: FeedbackAthleteAnswerProvider,
        private route: ActivatedRoute,
        private breadcrumbProvider: BreadcrumbProvider,
        private translator: TranslateService,
        private userProvider: UsersProvider,
        private cd: ChangeDetectorRef,
        private arrayUtils: ArrayUtils,
        private utils: UtilsProvider,
        private currentUserRoles: CurrentUserRolesProvider,
        private currentUser: CurrentUser,
        private title: Title,
        public conf: ConfigProvider,
        public datepipe: AppDatePipe,
        private api: ApiBaseService
    ) {
        this.factoryResolver = factoryResolver;
    }

    public ngOnInit(): void {
        this.subscriptions.push(
            this.route.data.subscribe((data: any) => { //{ organization: Organization; athlete: Athlete; feedbackAthleteAnswer: any }

                this.organization = data.organization;
                this.athleteUser = data.athlete;
                this.feedbackAthleteAnswer = data.feedbackAthleteAnswer;

                this.feedbackAthleteAnswer.feedback.rights = mapFeedbackItemRightsToRowRights(this.feedbackAthleteAnswer.feedback.rights ?? []);

                this.rights.evaluateScreenRights({
                    rolesIds: this.currentUserRoles.getRolesIds(this.organization.id!)
                });

                if (!this.rights.LIST) {
                    this.is401 = true;
                }

                if (!this.feedbackAthleteAnswer.feedback.rights.view) {
                    this.is401 = true;
                }

                this.createPageTitle();
                this.createBreadcrumb();
                this.isLoading = false;
            })
        );
    }

    public async ionViewDidLoad() {
        this.subscriptions.push(
            this.athleteAnswerRefs.changes.subscribe(() => {
                this.loadComponents();
            })
        );

        await this.loadDeposits();
    }

    public async loadDeposits() {
        const grouped = this.arrayUtils.groupBy(this.feedbackAthleteAnswer.answers, (a) => a.feedbackQuestionId);
        this.feedbackAthleteAnswer['answersGrouped'] = grouped;
        this.answers = grouped;
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public ngAfterViewInit() {
        this.ionViewDidLoad();
        this.cd.detectChanges();
    }

    private loadComponents() {
        this.athleteAnswerRefs.forEach((viewContainerRef, i) => {
            // get map item by Map index, not by his key
            const mapItem = this.answers.get(Array.from(this.answers.keys())[i]);

            // map item is an array
            const type = mapItem[0].questionType;

            const componentFactory = this.factoryResolver.resolveComponentFactory(ATHLETE_ANSWER_COMPONENTS_MAP[type]);
            this.componentRef = viewContainerRef.createComponent(componentFactory, undefined, viewContainerRef.injector);
            // this.componentRef.changeDetectorRef.detectChanges();
            const component = this.componentRef.instance as BaseAthleteAnswer;
            component.data = mapItem;
        });

        this.cd.detectChanges();
    }

    private createPageTitle() {
        this.pageTitle = this.translator.instant('FeedbackOf').replace('{{date}}', this.datepipe.transform(this.feedbackAthleteAnswer.feedback.answeredAt, 'mediumDate'));
        this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {

        const crumbs: Crumb[] = [];
        if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id!)) {
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: `${this.athleteUser.firstName?.substr(0, 1)}. ${this.athleteUser.lastName}`,
            title: `${this.athleteUser.firstName} ${this.athleteUser.lastName}`,
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}`
        });
        crumbs.push({
            shortTitle: 'Feedback',
            title: 'Feedback',
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}/feedback`
        });
        crumbs.push({
            shortTitle: this.translator.instant(this.feedbackAthleteAnswer.feedback.sessionName || this.feedbackAthleteAnswer.feedback.feedbackName),
            title: this.translator.instant(this.feedbackAthleteAnswer.feedback.sessionName || this.feedbackAthleteAnswer.feedback.feedbackName),
            url: ''
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }
}
